import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, RefreshCw, Check, X, Copy, Play, Power, Loader2, DatabaseBackup, CheckCircle, XCircle, HelpCircle, ChevronLeft, ChevronRight } from 'lucide-react';
// import type { VM, Ambiente } from '@prisma/client'; // Removido temporariamente
import { useToast } from '../../contexts/ToastContext';
import api from '../../services/api';

// Removido temporariamente
// type VMWithAmbiente = VM & {
//   ambiente: Ambiente;
// };
type VMWithAmbiente = any; // Usando any temporariamente
type Ambiente = any; // Definindo tipo Ambiente como any

type EditingVM = {
  id: string;
  field: 'start_time' | 'stop_time';
  value: string;
};

// Interface para resposta paginada
interface PaginatedResponse {
  data: VMWithAmbiente[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Função auxiliar para formatar RAM (MB para GB)
const formatRam = (ramInMB: number | null | undefined): string => {
  if (typeof ramInMB !== 'number') return '-';
  return `${(ramInMB / 1024).toFixed(1)} GB`;
};

export const VMList = () => {
  const [vms, setVMs] = useState<VMWithAmbiente[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [editingVM, setEditingVM] = useState<EditingVM | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [processingVm, setProcessingVm] = useState<{ id: string; action: 'start' | 'stop' | 'backup' } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { addToast } = useToast();

  // Estados para filtros
  const [ambientes, setAmbientes] = useState<Ambiente[]>([]);
  const [selectedAmbiente, setSelectedAmbiente] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

  // Buscar Ambientes
  useEffect(() => {
    const fetchAmbientes = async () => {
      try {
        // Assumir que a API retorna um objeto com uma propriedade 'data' contendo o array
        const response = await api.get<{ data: Ambiente[] }>('/api/ambientes'); // Ajustar tipo esperado
        // Verificar se response.data e response.data.data existem e se response.data.data é um array
        if (response.data && Array.isArray(response.data.data)) {
            setAmbientes(response.data.data);
        } else {
            console.error('API /api/ambientes não retornou a estrutura esperada { data: [...] }:', response.data);
            setAmbientes([]); // Define como array vazio em caso de formato inesperado
        }
      } catch (error) {
        console.error('Erro ao buscar ambientes:', error);
        addToast({ type: 'error', title: 'Erro ao carregar ambientes para filtro' });
        setAmbientes([]); // Define como array vazio em caso de erro na busca
      }
    };
    fetchAmbientes();
  }, [addToast]);

  // Debounce para o searchTerm
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // Atraso de 500ms

    return () => {
      clearTimeout(timerId); // Limpa o timer se o usuário digitar novamente
    };
  }, [searchTerm]);

  // Buscar VMs
  useEffect(() => {
    fetchVMs(1);
  }, [selectedAmbiente, debouncedSearchTerm]);

  const fetchVMs = async (page = 1) => {
    setIsRefreshing(true);
    try {
      const params: any = {
        page,
        pageSize,
      };
      if (selectedAmbiente) {
        params.ambienteId = selectedAmbiente;
      }
      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }

      const response = await api.get<PaginatedResponse>('/api/vms', { params });
      setVMs(response.data.data);
      setTotalItems(response.data.total);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.page);
    } catch (error) {
      console.error('Error fetching VMs:', error);
      addToast({ type: 'error', title: 'Erro ao buscar VMs' });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handler para mudar de página
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    fetchVMs(page);
  };

  // Handlers para mudança nos filtros
  const handleAmbienteChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedAmbiente(event.target.value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta VM?')) return;

    try {
      const response = await api.delete(`/api/vms/${id}`);
      
      if (response.status === 204) {
        fetchVMs(currentPage);
        addToast({
          type: 'success',
          title: 'VM excluída com sucesso!'
        });
      } else {
        console.error('Erro ao excluir VM, status:', response.status, response.data);
        addToast({
          type: 'error',
          title: 'Erro ao excluir VM',
          description: `Status: ${response.status}`
        });
      }
    } catch (error: any) {
      console.error('Error deleting VM:', error);
      const errorMessage = error.response?.data?.error || 'Erro desconhecido ao excluir VM.';
      addToast({
        type: 'error',
        title: 'Erro ao excluir VM',
        description: errorMessage
      });
    }
  };

  const handleSyncMGC = async () => {
    setIsSyncing(true);
    addToast({ type: 'info', title: 'Iniciando sincronização de VMs...' });
    try {
      const response = await api.post('/api/vms/sync');
      if (response.status === 200) {
        await fetchVMs(1); // Voltar para a primeira página após sincronização
        addToast({ type: 'success', title: 'Sincronização de VMs concluída!', description: 'Lista atualizada.' });
      } else {
        console.error('Falha na sincronização, status:', response.status, response.data);
        const errorDesc = response.data?.error || `Status: ${response.status}`;
        addToast({ type: 'error', title: 'Falha na sincronização', description: errorDesc });
        throw new Error('Falha na sincronização');
      }
    } catch (error: any) {
      console.error('Erro ao sincronizar com MGC:', error);
      if (!error.response) {
        addToast({ type: 'error', title: 'Erro de rede', description: 'Não foi possível conectar ao servidor para sincronizar.' });
      }
    } finally {
      setIsSyncing(false);
    }
  };

  const formatWeekdays = (weekdays: string[] | undefined | null) => {
    if (!weekdays || weekdays.length === 0) {
      return 'Nenhum dia';
    }
    const labels: Record<string, string> = {
      mon: 'Seg',
      tue: 'Ter',
      wed: 'Qua',
      thu: 'Qui',
      fri: 'Sex',
      sat: 'Sáb',
      sun: 'Dom',
    };
    return weekdays.map(day => labels[day.toLowerCase()] || day).join(', ');
  };

  const getRegiaoLabel = (regiao: string) => {
    const labels = {
      br_se1: 'BR-SE1',
      br_mgl1: 'BR-MGL1',
      br_ne1: 'BR-NE1',
    };
    return labels[regiao as keyof typeof labels] || regiao;
  };

  const getStateLabel = (state: string) => {
    const labels: Record<string, string> = {
      'running': 'Em execução',
      'stopped': 'Parada',
      'starting': 'Iniciando',
      'stopping': 'Parando',
      'unknown': 'Desconhecido'
    };
    return labels[state] || state;
  };

  const getStateColor = (state: string) => {
    const colors: Record<string, string> = {
      'running': 'text-green-600',
      'stopped': 'text-red-600',
      'starting': 'text-yellow-600',
      'stopping': 'text-yellow-600',
      'unknown': 'text-gray-600'
    };
    return colors[state] || 'text-gray-600';
  };

  const handleStartEdit = (vm: VMWithAmbiente, field: 'start_time' | 'stop_time') => {
    setEditingVM({
      id: vm.id,
      field,
      value: vm[field] || '',
    });
  };

  const handleCancelEdit = () => {
    setEditingVM(null);
  };

  const handleSaveEdit = async () => {
    if (!editingVM) return;

    try {
      const payload = { [editingVM.field]: editingVM.value };
      const response = await api.patch(`/api/vms/${editingVM.id}`, payload);

      if (response.status === 200) {
        await fetchVMs(currentPage);
        addToast({
          type: 'success',
          title: 'Horário atualizado com sucesso!'
        });
        setEditingVM(null);
      } else {
        console.error('Falha ao atualizar horário, status:', response.status, response.data);
        const errorDesc = response.data?.error || `Status: ${response.status}`;
        addToast({ type: 'error', title: 'Falha ao atualizar horário', description: errorDesc });
        throw new Error('Falha ao atualizar horário');
      }
    } catch (error: any) {
      console.error('Erro ao atualizar horário:', error);
       const errorMessage = error.response?.data?.error || 'Erro desconhecido ao atualizar horário.';
      addToast({
        type: 'error',
        title: 'Erro ao atualizar horário',
        description: errorMessage
      });
    }
  };

  const handleCopyId = async (text: string, copyKey: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(copyKey);
      addToast({
        type: 'success',
        title: 'Copiado com sucesso!'
      });
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao copiar'
      });
    }
  };

  const handleVmAction = async (id: string, action: 'start' | 'stop') => {
    setProcessingVm({ id, action });
    const actionVerb = action === 'start' ? 'ligar' : 'desligar';

    try {
      const response = await api.post(`/api/vms/${id}/${action}`);

      if (response.status === 200) {
        addToast({ type: 'success', title: `Comando para ${actionVerb} VM enviado com sucesso!` });
        await fetchVMs(currentPage);
      } else {
        console.error(`Erro ao ${actionVerb} VM, status:`, response.status, response.data);
        const errorDesc = response.data?.error || `Status: ${response.status}`;
        addToast({ type: 'error', title: `Falha ao ${actionVerb} VM`, description: errorDesc });
        throw new Error(errorDesc || `Falha ao ${actionVerb} VM`);
      }
    } catch (error: any) {
      console.error(`Erro ao ${actionVerb} VM:`, error);
      if (!error.response) {
         addToast({ type: 'error', title: `Erro ao ${actionVerb} VM` });
      }
    } finally {
      setProcessingVm(null);
    }
  };

  const handleBackupVm = async (id: string, nome: string) => {
    setProcessingVm({ id, action: 'backup' });
    addToast({ type: 'info', title: `Iniciando backup para ${nome}...` });
    try {
      const response = await api.post(`/api/vms/${id}/snapshot`);

      if (response.status === 201) {
        console.log("Resposta API Snapshot:", response.data);
        addToast({ type: 'success', title: 'Comando de backup enviado com sucesso!', description: response.data?.message });
      } else {
        console.error('Erro ao solicitar backup, status:', response.status, response.data);
        const errorMessage = response.data?.error || response.data?.message || 'Falha ao solicitar backup';
        addToast({ type: 'error', title: 'Erro no backup', description: errorMessage });
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error(`Erro ao solicitar backup para VM ${nome}:`, error);
      if (!error.response) {
        addToast({ type: 'error', title: 'Erro no backup' });
      }
    } finally {
      setProcessingVm(null);
    }
  };

  return (
    <div>
       {/* Cabeçalho com Título e Botões de Ação */}
       <div className="flex justify-between items-center mb-4">
         <h1 className="text-2xl font-bold text-gray-900">VMs</h1>
         {/* Botões de Ação */}
         <div className="flex gap-2">
            <button
             onClick={handleSyncMGC}
             disabled={isSyncing}
             className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
             <RefreshCw className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
             Sincronizar MGC
            </button>
          </div>
       </div>

       {/* Linha de Filtros */}
       <div className="flex gap-4 mb-6 items-end">
           {/* Filtro de Busca com Label */}
           <div className="w-64"> {/* Wrapper para label + input */}
             <label htmlFor="vm-search" className="block text-sm font-medium text-gray-700 mb-1">
               Buscar por nome
             </label>
             <div className="relative"> {/* Manter div relativo para o ícone X */}
               <input
                 type="text"
                 id="vm-search" /* Adicionar ID */
                 placeholder="Digite o nome..." /* Placeholder pode ser mais genérico */
                 value={searchTerm}
                 onChange={handleSearchChange}
                 className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 pr-8"
                 disabled={isRefreshing || isSyncing}
               />
               {searchTerm && (
                 <button 
                   type="button"
                   onClick={() => setSearchTerm('')} 
                   className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400 hover:text-gray-600"
                   title="Limpar busca"
                 >
                   <X className="h-4 w-4" />
                 </button>
               )}
             </div>
           </div>

           {/* Filtro de Ambiente com Label */}
           <div className="w-64"> {/* Wrapper para label + select */}
             <label htmlFor="vm-ambiente-filter" className="block text-sm font-medium text-gray-700 mb-1">
               Ambiente
             </label>
             <div className="relative"> {/* Manter div relativo para os ícones */}
               <select
                 id="vm-ambiente-filter" /* Adicionar ID */
                 value={selectedAmbiente}
                 onChange={handleAmbienteChange}
                 className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 pr-12 appearance-none"
                 disabled={isRefreshing || isSyncing}
               >
                 <option value="">Todos Ambientes</option>
                 {ambientes.map((ambiente) => (
                   <option key={ambiente.id} value={ambiente.id}>
                     {ambiente.nome}
                   </option>
                 ))}
               </select>
               {/* Ícone Limpar (X) */}
               {selectedAmbiente && (
                 <button 
                   type="button"
                   onClick={() => setSelectedAmbiente('')} 
                   className="absolute inset-y-0 right-7 flex items-center pr-2 text-gray-400 hover:text-gray-600"
                   title="Limpar seleção"
                 >
                   <X className="h-4 w-4" />
                 </button>
               )}
               {/* Seta Dropdown Customizada */}
               <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                   <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
               </div>
             </div>
           </div>
       </div>

       {/* Tabela de VMs */}
       <div className="bg-white rounded-lg shadow overflow-hidden">
         <table className="min-w-full divide-y divide-gray-200">
           <thead className="bg-gray-50">
             <tr>
               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                 Nome
               </th>
               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                 Ambiente
               </th>
               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                 Região
               </th>
               <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                 Estado
               </th>
               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                 Start/Stop
               </th>
               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                 Snapshot
               </th>
               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                 Status
               </th>
               <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                 Ações
               </th>
             </tr>
           </thead>
           <tbody className="bg-white divide-y divide-gray-200">
             {vms.map((vm) => {
               const isProcessingThisVm = processingVm?.id === vm.id;
               const currentAction = processingVm?.action;
               const isLoadingStart = isProcessingThisVm && currentAction === 'start';
               const isLoadingStop = isProcessingThisVm && currentAction === 'stop';
               const isLoadingBackup = isProcessingThisVm && currentAction === 'backup';
               const canStart = vm.state === 'stopped';
               const canStop = vm.state === 'running';
               const canBackup = vm.state === 'running' || vm.state === 'stopped';

               return (
                 <tr key={vm.id} className="hover:bg-gray-50">
                   <td className="px-6 py-4">
                     <div className="flex items-center mb-1">
                       <span className="text-sm font-medium text-gray-900 mr-2">{vm.nome}</span>
                       <button
                         onClick={() => handleCopyId(vm.magalu_id, `${vm.id}-magalu_id`)}
                         className="text-gray-400 hover:text-gray-600"
                         title={`Copiar ID Magalu: ${vm.magalu_id}`}
                       >
                         {copiedId === `${vm.id}-magalu_id` ? (
                           <Check className="w-3 h-3 text-green-600" />
                         ) : (
                           <Copy className="w-3 h-3" />
                         )}
                       </button>
                     </div>
                     <div className="text-xs text-gray-500 space-x-2">
                       <span>
                         CPU: {vm.tipoVm?.vcpus || '-'}
                       </span>
                       <span>
                         RAM: {formatRam(vm.tipoVm?.ram)}
                       </span>
                       <span>
                         Disco: {vm.tipoVm?.disk ? `${vm.tipoVm.disk} GB` : '-'}
                       </span>
                       {vm.tipoVm?.gpu && (
                          <span>GPU: {vm.tipoVm.gpu}</span>
                       )}
                     </div>
                     <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                       <div className="flex items-center gap-1">
                         <span>IP Privado: {vm.private_ip || '-'}</span>
                         {vm.private_ip && (
                           <button
                             onClick={() => handleCopyId(vm.private_ip, `${vm.id}-private_ip`)}
                             className="text-gray-400 hover:text-gray-600"
                             title={`Copiar IP privado: ${vm.private_ip}`}
                           >
                             {copiedId === `${vm.id}-private_ip` ? (
                               <Check className="w-3 h-3 text-green-600" />
                             ) : (
                               <Copy className="w-3 h-3" />
                             )}
                           </button>
                         )}
                       </div>
                       <div className="flex items-center gap-1">
                         <span>IP Público: {vm.public_ip || '-'}</span>
                         {vm.public_ip && (
                           <button
                             onClick={() => handleCopyId(vm.public_ip, `${vm.id}-public_ip`)}
                             className="text-gray-400 hover:text-gray-600"
                             title={`Copiar IP público: ${vm.public_ip}`}
                           >
                             {copiedId === `${vm.id}-public_ip` ? (
                               <Check className="w-3 h-3 text-green-600" />
                             ) : (
                               <Copy className="w-3 h-3" />
                             )}
                           </button>
                         )}
                       </div>
                     </div>
                   </td>
                   <td className="px-6 py-4">
                     <div className="text-sm text-gray-900">{vm.ambiente?.nome || '-'}</div>
                   </td>
                   <td className="px-6 py-4">
                     <div className="text-sm text-gray-900">
                       {getRegiaoLabel(vm.regiao)}
                     </div>
                   </td>
                   <td className="px-6 py-4 whitespace-nowrap text-center">
                     {(() => {
                       const state = vm.state || 'unknown';
                       const color = getStateColor(state);
                       const label = getStateLabel(state);
                       const commonProps = { className: `w-5 h-5 ${color}` };

                       switch (state) {
                         case 'running':
                           return <span title={label}><CheckCircle {...commonProps} /></span>;
                         case 'stopped':
                           return <span title={label}><XCircle {...commonProps} /></span>;
                         case 'starting':
                         case 'stopping':
                           return <span title={label}><Loader2 {...commonProps} className={`${commonProps.className} animate-spin`} /></span>;
                         case 'unknown':
                         default:
                           return <span title={label}><HelpCircle {...commonProps} /></span>;
                       }
                     })()}
                   </td>
                   <td className="px-6 py-4">
                     <div className="text-sm text-gray-900">
                       {`${vm.start_time || '-'} - ${vm.stop_time || '-'}`}
                     </div>
                     <div className="text-xs text-gray-500 mt-1">
                       {formatWeekdays(vm.weekdays)}
                     </div>
                   </td>
                   <td className="px-6 py-4">
                     <div className="text-sm text-gray-900">
                      {vm.snapshot_time || '-'}
                     </div>
                     <div className="text-xs text-gray-500 mt-1">
                       {formatWeekdays(vm.snapshot_weekdays)}
                     </div>
                     <div className="text-xs text-gray-500 mt-1">
                       Retenção: {vm.snapshot_retention_limit != null ? vm.snapshot_retention_limit : 'Sem limite'}
                     </div>
                   </td>
                   <td className="px-6 py-4 whitespace-nowrap">
                     <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                       vm.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                     }`}>
                       {vm.ativo ? 'Ativo' : 'Inativo'}
                     </span>
                   </td>
                   <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                     <div className="flex justify-end items-center gap-3">
                       {canStart && (
                         <button
                           onClick={() => handleVmAction(vm.id, 'start')}
                           disabled={isLoadingStart || isLoadingStop || isLoadingBackup || isSyncing || isRefreshing || !canBackup}
                           className={`text-green-600 hover:text-green-800 disabled:opacity-50 disabled:cursor-not-allowed`}
                           title="Ligar VM"
                         >
                           {isLoadingStart ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                         </button>
                       )}
                       {canStop && (
                         <button
                           onClick={() => handleVmAction(vm.id, 'stop')}
                           disabled={isLoadingStart || isLoadingStop || isLoadingBackup || isSyncing || isRefreshing || !canBackup}
                           className={`text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed`}
                           title="Desligar VM"
                         >
                           {isLoadingStop ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
                         </button>
                       )}
                       <button
                         onClick={() => handleBackupVm(vm.id, vm.nome)}
                         disabled={isLoadingStart || isLoadingStop || isLoadingBackup || isSyncing || isRefreshing || !canBackup}
                         className={`text-purple-600 hover:text-purple-800 disabled:opacity-50 disabled:cursor-not-allowed`}
                         title="Criar Backup (Snapshot)"
                       >
                         {isLoadingBackup ? <Loader2 className="w-4 h-4 animate-spin" /> : <DatabaseBackup className="w-4 h-4" />}
                       </button>
                       <Link 
                         to={`/vms/${vm.id}`} 
                         className={`text-blue-600 hover:text-blue-900 ${isLoadingStart || isLoadingStop || isLoadingBackup ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                         title="Editar Agendamento"
                       >
                         <Pencil className="w-4 h-4" />
                       </Link>
                       <button 
                         onClick={() => handleDelete(vm.id)} 
                         className={`text-gray-500 hover:text-gray-700 ${isLoadingStart || isLoadingStop || isLoadingBackup ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
                         disabled={isLoadingStart || isLoadingStop || isLoadingBackup || isSyncing || isRefreshing}
                         title="Excluir Registro da VM"
                       >
                         <Trash2 className="w-4 h-4" />
                       </button>
                     </div>
                   </td>
                 </tr>
               );
             })}
           </tbody>
         </table>
         
         {/* Paginação */}
         <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
           <div className="flex-1 flex justify-between sm:hidden">
             <button
               onClick={() => handlePageChange(currentPage - 1)}
               disabled={currentPage <= 1}
               className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
             >
               Anterior
             </button>
             <button
               onClick={() => handlePageChange(currentPage + 1)}
               disabled={currentPage >= totalPages}
               className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
             >
               Próximo
             </button>
           </div>
           <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
             <div>
               <p className="text-sm text-gray-700">
                 Mostrando <span className="font-medium">{vms.length > 0 ? (currentPage - 1) * pageSize + 1 : 0}</span> a <span className="font-medium">{Math.min(currentPage * pageSize, totalItems)}</span> de <span className="font-medium">{totalItems}</span> VMs
               </p>
             </div>
             <div>
               <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Paginação">
                 <button
                   onClick={() => handlePageChange(currentPage - 1)}
                   disabled={currentPage <= 1}
                   className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                   <span className="sr-only">Anterior</span>
                   <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                 </button>
                 
                 {/* Páginas numéricas */}
                 {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                   const pageNum = Math.max(1, Math.min(
                     totalPages - 4, 
                     currentPage - 2
                   )) + i;
                   
                   if (pageNum <= totalPages) {
                     return (
                       <button
                         key={pageNum}
                         onClick={() => handlePageChange(pageNum)}
                         className={`relative inline-flex items-center px-4 py-2 border ${currentPage === pageNum 
                           ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' 
                           : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                         } text-sm font-medium`}
                       >
                         {pageNum}
                       </button>
                     );
                   }
                   return null;
                 })}
                 
                 <button
                   onClick={() => handlePageChange(currentPage + 1)}
                   disabled={currentPage >= totalPages}
                   className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                   <span className="sr-only">Próximo</span>
                   <ChevronRight className="h-5 w-5" aria-hidden="true" />
                 </button>
               </nav>
             </div>
           </div>
         </div>
       </div>
    </div>
  );
};